import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FaRegFileVideo, FaRegFileAlt } from "react-icons/fa";
import imageCompression from "browser-image-compression";
import { iSValidFileSize } from "../../../utils/canvasUtils";
import {
  IMAGE_SIZE_LIMIT_IN_MB,
  VIDEO_SIZE_LIMT_IN_MB,
  DOCUMENT_SIZE_LIMIT_IN_MB,
} from "../../../components/MultiInput/multiInput-constant";
import { API_GET_FILE_URL } from "../../../api/media.service";
import CustomProgressBar from "../../../components/CustomProgressBar";
import { Image } from "primereact/image";
import { Text } from "../../../components/Text";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";

const FileInputComponent = ({ setMediaFile, mediaFile, template }) => {
  const { templateType } = template;
  const [activeIndex, setActiveIndex] = useState(0);
  const [fileChoosen, setFileChoosen] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const mediaFileRefQuick = useRef(null);
  const [isUploading, setUploading] = useState(false);
  const toast = useRef(null);
  const [documentModal, setDocumentModal] = useState(false);
  const [documentName, setDocumentName] = useState(null);
  const onHide = () => {
    setDocumentModal(false);
  };
  useEffect(() => {
    switch (templateType.toUpperCase()) {
      case "IMAGE":
        setActiveIndex(1);
        setImagePreview(mediaFile?.image);
        break;
      case "VIDEO":
        setActiveIndex(2);
        setVideoPreview(mediaFile?.video);
        break;
      case "DOCUMENT":
        setActiveIndex(3);
        setDocumentPreview(mediaFile?.document);
        break;
      default:
        setActiveIndex(0);
    }
  }, [templateType, mediaFile]);

  const errorToast = (message) => {
    if (toast.current) {
      toast.current.show({ severity: "error", detail: message });
    } else {
      console.error("Toast component is not mounted:", message);
    }
  };

  const handleUpload = async (e) => {
    try {
      let file = e.target.files[0];
      if (file) {
        setMediaFile(null);
        if (file.type.startsWith("image/")) {
          if (!iSValidFileSize(file.size, IMAGE_SIZE_LIMIT_IN_MB)) {
            errorToast(
              `Image file size should not be more than ${IMAGE_SIZE_LIMIT_IN_MB} MB.`
            );
            return;
          }
          const options = {
            maxSizeMB: IMAGE_SIZE_LIMIT_IN_MB,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          };
          const compressedFile = await imageCompression(file, options);
          setFileChoosen(
            new File([compressedFile], compressedFile.name, {
              type: compressedFile.type,
            })
          );
        } else if (file.type.startsWith("video/")) {
          if (!iSValidFileSize(file.size, VIDEO_SIZE_LIMT_IN_MB)) {
            errorToast(
              `Video file size should not be more than ${VIDEO_SIZE_LIMT_IN_MB} MB.`
            );
            return;
          }
          setFileChoosen(file);
        } else if (file.type === "application/pdf") {
          if (!iSValidFileSize(file.size, DOCUMENT_SIZE_LIMIT_IN_MB)) {
            errorToast(
              `Document file size should not be more than ${DOCUMENT_SIZE_LIMIT_IN_MB} MB.`
            );
            return;
          }
          setFileChoosen(file);
          setDocumentPreview(URL?.createObjectURL(file));
          setDocumentName(file?.name);
        }
      }
    } catch (error) {
      console.error("Error occurred during file upload:", error);
      errorToast("Error occurred during upload");
    }
  };

  const uploadFile = async () => {
    const resp = await API_GET_FILE_URL();
    const fileType =
      activeIndex === 1
        ? "image/png"
        : activeIndex === 2
        ? "video/mp4"
        : "application/pdf";

    const config = {
      onUploadProgress: (progressEvent) => {
        setUploadPercentage(
          Math.round((progressEvent.loaded * 100) / progressEvent.total)
        );
      },
      headers: { "Content-Type": fileType },
    };

    await axios.put(resp.data.src, fileChoosen, config);
    const _data = {
      [activeIndex === 1 ? "image" : activeIndex === 2 ? "video" : "document"]:
        resp.data.url,
    };
    setMediaFile(_data);
    if (activeIndex === 1) setImagePreview(resp.data.url);
    else if (activeIndex === 2) setVideoPreview(resp.data.url);
    else if (activeIndex === 3) setDocumentPreview(resp.data.url);
  };

  useEffect(() => {
    if (fileChoosen && activeIndex !== 0) {
      setUploading(true);
      uploadFile()
        .then(() => {
          setUploading(false);
          setUploadPercentage(0);
        })
        .catch(() => setUploading(false));
    }
  }, [fileChoosen, activeIndex]);

  return (
    <>
      <Toast ref={toast} />
      {activeIndex !== 0 && (
        <div className="mt-3">
          <input
            ref={mediaFileRefQuick}
            type="file"
            accept={
              activeIndex === 1
                ? "image/*"
                : activeIndex === 2
                ? "video/*"
                : ".pdf"
            }
            hidden
            onChange={handleUpload}
          />
          <button
            className="w-full flex justify-center gap-3 p-3 cursor-pointer border border-dashed bg-gray-200"
            onClick={() => mediaFileRefQuick?.current?.click()}
          >
            {activeIndex === 1 ? (
              <i className="pi pi-image" />
            ) : activeIndex === 2 ? (
              <FaRegFileVideo />
            ) : (
              <FaRegFileAlt />
            )}
            <Text type="sub-heading">
              Upload{" "}
              {activeIndex === 1
                ? "Image"
                : activeIndex === 2
                ? "Video"
                : "Document"}
            </Text>
          </button>
        </div>
      )}
      {isUploading && <CustomProgressBar progress={uploadPercentage} />}
      {activeIndex === 2 && videoPreview && (
        <video controls width="100%" className="mt-2">
          <source src={videoPreview} type="video/mp4" />
        </video>
      )}
      {activeIndex === 1 && imagePreview && (
        <Image src={imagePreview} preview width="100%" className="mt-2" />
      )}
      {activeIndex === 3 && documentPreview && (
        <div className="mt-2">
          {documentName && (
            <Text type="sub-heading">Uploaded PDF: {documentName}</Text>
          )}
          <div
            onClick={() => setDocumentModal(true)}
            className="border rounded-lg shadow-md p-2 bg-gray-100 cursor-pointer hover:shadow-lg transition  flex-col justify-center items-center"
          >
            <div
              style={{ width: "250px", height: "150px", overflow: "hidden" }}
            >
              <embed
                src={`${documentPreview}#toolbar=0`}
                type="application/pdf"
                width="100%"
                height="600px"
              />
            </div>
            <div>
              <p className="text-center text-sm text-blue-500 mt-2 cursor-pointer">
                Click to View Full Document
              </p>
            </div>
          </div>

          {documentModal && (
            <Dialog
              header="Document Preview"
              visible={documentModal}
              onHide={() => setDocumentModal(false)}
              style={{ width: "80vw" }}
              className="shadow-lg rounded-lg"
              modal
              draggable={false}
            >
              <div className="flex flex-col items-center p-4">
                <embed
                  src={documentPreview}
                  type="application/pdf"
                  width="100%"
                  height="600px"
                />
                ;
              </div>
            </Dialog>
          )}
        </div>
      )}
    </>
  );
};

export default FileInputComponent;
